import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { campaignKeys } from '../services/campaign.keys';

export interface InfluencerPaymentDetail {
  influencerName: string;
  influencerHandle: string;
  totalFee: number;
  amountPaid: number;
  amountDue: number;
}

export interface CampaignBudgetData {
  totalBudget: number;
  totalInfluencerPayments: number;
  totalExpenses: number;
  amountSpent: number;
  remainingBudget: number;
  influencerPaymentDetails: InfluencerPaymentDetail[];
}

interface RpcBudgetResponse {
  total_budget: number;
  total_influencer_payments: number;
  total_expenses: number;
  amount_spent: number;
  remaining_budget: number;
  influencer_payment_details: Array<{
    influencer_name: string;
    influencer_handle: string;
    total_fee: number;
    amount_paid: number;
    amount_due: number;
  }>;
}

export const useCampaignBudgetCalculations = (campaignId: string) => {
  const { currentOrganization } = useOrganization();

  return useQuery<CampaignBudgetData>({
    queryKey: campaignKeys.budget(campaignId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_campaign_budget_calculations', {
        p_campaign_id: campaignId,
      });
      if (error) throw error;

      const result = data as unknown as RpcBudgetResponse;

      return {
        totalBudget: result.total_budget,
        totalInfluencerPayments: result.total_influencer_payments,
        totalExpenses: result.total_expenses,
        amountSpent: result.amount_spent,
        remainingBudget: result.remaining_budget,
        influencerPaymentDetails: (result.influencer_payment_details || []).map((d) => ({
          influencerName: d.influencer_name,
          influencerHandle: d.influencer_handle,
          totalFee: d.total_fee,
          amountPaid: d.amount_paid,
          amountDue: d.amount_due,
        })),
      };
    },
    enabled: !!campaignId && !!currentOrganization?.id,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};
